import type { ProgramSlideLayoutV1 } from '../types/programContentSlideCanvas';
import {
  normalizeProgramSlideRegistryName,
  PROGRAM_SLIDE_REGISTERED_PAGE_MAX_COUNT,
  programSlideRegistryFallbackName,
} from './programSlideRegistryLimits';

const STORAGE_PREFIX = 'cp.programSlideRegisteredPages.v1:';
export const PROGRAM_SLIDE_REGISTERED_PAGES_CHANGED_EVENT =
  'cp:program-slide-registered-pages-changed';

export interface ProgramSlideRegisteredPage {
  id: string;
  name: string;
  layout: ProgramSlideLayoutV1;
  order: number;
  updatedAt: number;
}

function storageKey(workspaceId: string): string {
  return `${STORAGE_PREFIX}${workspaceId.trim()}`;
}

function safeStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function normalizeName(raw: unknown, fallback: string): string {
  return normalizeProgramSlideRegistryName(raw, fallback);
}

function normalizePages(raw: unknown): ProgramSlideRegisteredPage[] {
  if (!Array.isArray(raw)) return [];
  const out: ProgramSlideRegisteredPage[] = [];
  raw.forEach((item, idx) => {
    if (!item || typeof item !== 'object') return;
    const o = item as {
      id?: unknown;
      name?: unknown;
      layout?: unknown;
      order?: unknown;
      updatedAt?: unknown;
    };
    const id = String(o.id ?? '').trim();
    if (!id) return;
    out.push({
      id,
      name: normalizeName(o.name, programSlideRegistryFallbackName(idx)),
      layout: (o.layout ?? {}) as ProgramSlideLayoutV1,
      order:
        Number.isFinite(Number(o.order)) && Number(o.order) >= 0
          ? Number(o.order)
          : idx,
      updatedAt:
        Number.isFinite(Number(o.updatedAt)) && Number(o.updatedAt) > 0
          ? Number(o.updatedAt)
          : Date.now(),
    });
  });
  return out.sort((a, b) => a.order - b.order);
}

function emitChanged(workspaceId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(
      new CustomEvent(PROGRAM_SLIDE_REGISTERED_PAGES_CHANGED_EVENT, {
        detail: { workspaceId: workspaceId.trim() },
      })
    );
  } catch {
    /* ignore */
  }
}

export function loadProgramSlideRegisteredPages(
  workspaceId: string
): ProgramSlideRegisteredPage[] {
  const sid = workspaceId.trim();
  if (!sid) return [];
  const st = safeStorage();
  if (!st) return [];
  try {
    const raw = st.getItem(storageKey(sid));
    if (!raw) return [];
    return normalizePages(JSON.parse(raw)).slice(0, PROGRAM_SLIDE_REGISTERED_PAGE_MAX_COUNT);
  } catch {
    return [];
  }
}

export function saveProgramSlideRegisteredPages(
  workspaceId: string,
  pages: ProgramSlideRegisteredPage[],
  options?: { emitEvent?: boolean }
): void {
  const sid = workspaceId.trim();
  if (!sid) return;
  const st = safeStorage();
  if (!st) return;
  try {
    const payload = pages
      .map((p, idx) => ({
        ...p,
        order: idx,
        name: normalizeName(p.name, programSlideRegistryFallbackName(idx)),
        updatedAt: Number.isFinite(p.updatedAt) ? p.updatedAt : Date.now(),
      }))
      .filter((p) => p.id.trim());
    st.setItem(storageKey(sid), JSON.stringify(payload));
    // DB→LS 동기화 경로에서 emit하면 CHANGED→forceRefresh 무한 루프가 난다
    if (options?.emitEvent !== false) {
      emitChanged(sid);
    }
  } catch {
    /* ignore */
  }
}

